import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { formatReportType } from '../utils/safetyReportLabels';

const SEVERITY_STYLES = {
  critical: 'bg-red-600 text-white', urgent: 'bg-orange-500 text-white', routine: 'bg-slate-100 text-slate-700',
};

function formatType(id) {
  return formatReportType(id);
}

function auditText(value, preferredKeys = []) {
  if (value == null || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    for (const key of preferredKeys) {
      const candidate = value[key];
      if (typeof candidate === 'string' || typeof candidate === 'number') return String(candidate);
    }
    if (typeof value._id === 'string') return value._id;
    return 'Unknown';
  }
  return String(value);
}

function numericMetric(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function ReportedUserLink({ row, className = 'font-medium text-pink-600 hover:underline' }) {
  const name = row.reportedUser?.profileName || 'Unknown';
  if (row.accountDeleted) {
    const q = row.reportedUser?.email || row.reportedUserId || '';
    return (
      <Link
        to={`/deleted-accounts?search=${encodeURIComponent(q)}`}
        className={className}
      >
        {name}{' '}
        <span className="text-xs font-normal text-amber-700">(account deleted)</span>
      </Link>
    );
  }
  return (
    <Link to={`/users/${row.reportedUserId}`} className={className}>
      {name}
    </Link>
  );
}

function Reports() {
  const [view, setView] = useState('safety');
  const [safetyReports, setSafetyReports] = useState([]);
  const [pendingHighCount, setPendingHighCount] = useState(0);
  const [reportedUsers, setReportedUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [feedReports, setFeedReports] = useState([]);
  const [feedTargetFilter, setFeedTargetFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [selectedSafety, setSelectedSafety] = useState(null);
  const [safetyDetail, setSafetyDetail] = useState(null);
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionTaken, setActionTaken] = useState('');
  const [safetyStatus, setSafetyStatus] = useState('pending');

  useEffect(() => {
    if (view === 'safety' || view === 'safeMeet') fetchSafetyQueue();
    else if (view === 'users') fetchReportedUsers();
    else if (view === 'feed') fetchFeedReports();
    else fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, statusFilter, priorityFilter, severityFilter, feedTargetFilter, view]);

  const fetchSafetyQueue = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (severityFilter) params.severity = severityFilter;
      if (view === 'safeMeet') {
      params.reportType = 'unsafe_meetup';
    }
    const response = await adminAPI.getSafetyQueue(params);
      setSafetyReports(response.data.reports || []);
      setPendingHighCount(response.data.pendingHighCount || 0);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load safety queue');
    } finally {
      setLoading(false);
    }
  };

  const fetchReportedUsers = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const response = await adminAPI.getReportedUsers(params);
      setReportedUsers(response.data.reportedUsers || []);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load reported users');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const response = await adminAPI.getAllReports(params);
      setReports(response.data.reports || []);
      setTotalPages(response.data.pagination.pages);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeedReports = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (feedTargetFilter) params.targetType = feedTargetFilter;
      const response = await adminAPI.getFeedReports(params);
      setFeedReports(response.data.reports || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load feed reports');
    } finally {
      setLoading(false);
    }
  };

  const openSafetyDetail = async (report) => {
    setSelectedSafety(report);
    setShowSafetyModal(true);
    setSafetyDetail(null);
    setAdminNotes(report.adminNotes || '');
    setActionTaken(report.actionTaken || '');
    setSafetyStatus(report.status || 'pending');
    try {
      const response = await adminAPI.getSafetyReport(report._id);
      setSafetyDetail(response.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load report details');
    }
  };

  const submitSafetyModeration = async () => {
    if (!selectedSafety) return;
    try {
      const payload = {
        status: safetyStatus,
        adminNotes,
      };
      if (actionTaken) payload.actionTaken = actionTaken;
      await adminAPI.updateSafetyReport(selectedSafety._id, payload);
      alert('Safety report updated');
      setShowSafetyModal(false);
      setSelectedSafety(null);
      setSafetyDetail(null);
      fetchSafetyQueue();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update safety report');
    }
  };

  const handleStatusUpdate = async (reportId, newStatus, action, kind = 'content') => {
    try {
      if (kind === 'safety') {
        await adminAPI.updateSafetyReport(reportId, { status: newStatus });
      } else if (kind === 'feed') {
        await adminAPI.updateFeedReport(reportId, {
          status: newStatus,
          action: action || undefined,
        });
      } else {
        await adminAPI.updateReportStatus(reportId, {
          status: newStatus,
          action,
          reportKind: 'content',
        });
      }
      if (view === 'users') fetchReportedUsers();
      else if (view === 'content') fetchReports();
      else if (view === 'feed') fetchFeedReports();
      else fetchSafetyQueue();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to update report status');
    }
  };

  const handleUserAction = async (userId, action, data = {}) => {
    try {
      if (action === 'ban') {
        await adminAPI.banUser(userId, {
          banned: true,
          reasonCode: 'SAFETY',
          reason: data.reason || 'Multiple reports',
        });
        alert('User permanently banned. Profile data scrubbed.');
      } else if (action === 'suspend') {
        await adminAPI.updateUser(userId, { suspended: true, reasonCode: 'UNDER_REVIEW' });
        alert('User suspended temporarily (reversible).');
      } else if (action === 'delete') {
        if (window.confirm('Are you sure you want to delete this user?')) {
          await adminAPI.deleteUser(userId);
          alert('User deleted successfully');
        }
      } else if (action === 'dismiss') {
        if (selectedUser) {
          const promises = selectedUser.reports.map((report) => {
            if (report.type === 'safety') {
              return adminAPI.updateSafetyReport(report._id, {
                status: 'dismissed',
                actionTaken: 'dismiss',
              });
            }
            return adminAPI.updateReportStatus(report._id, {
              status: 'dismissed',
              reportKind: 'content',
            });
          });
          await Promise.all(promises);
          alert('All reports dismissed');
        }
      }
      if (view === 'users') fetchReportedUsers();
      else fetchSafetyQueue();
      setShowUserModal(false);
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${action} user`);
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (s === 'resolved' || s === 'action_taken' || s === 'actiontaken') return 'bg-green-100 text-green-700';
    if (s === 'dismissed' || s === 'reviewed') return 'bg-gray-100 text-gray-700';
    return 'bg-gray-100 text-gray-700';
  };

  const switchView = (next) => {
    setView(next);
    setPage(1);
    if ((next === 'safety' || next === 'safeMeet') && !statusFilter) setStatusFilter('pending');
  };

  const openUserModal = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-900">Reports & Safety</h1>
          <p className="text-gray-600 mt-1 text-sm">
            Safety (user reports), feed/community reports, and legacy content reports are separate queues.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-center mb-6">
        <div className="flex gap-2 bg-gray-100 rounded-xl p-1">
          <button
            type="button"
            onClick={() => switchView('safety')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              view === 'safety' ? 'bg-pink-500 text-white' : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            Safety Queue
            {pendingHighCount > 0 && (
              <span className="ml-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                {pendingHighCount} high
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => switchView('safeMeet')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              view === 'safeMeet' ? 'bg-pink-500 text-white' : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            Safe Meet Reports
          </button>
          <button
            type="button"
            onClick={() => switchView('users')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              view === 'users' ? 'bg-pink-500 text-white' : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            By User
          </button>
          <button
            type="button"
            onClick={() => switchView('feed')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              view === 'feed' ? 'bg-pink-500 text-white' : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            Feed Reports
          </button>
          <button
            type="button"
            onClick={() => switchView('content')}
            className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
              view === 'content' ? 'bg-pink-500 text-white' : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            Content Reports
          </button>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="reviewed">Reviewed</option>
          <option value="resolved">Resolved / Action taken</option>
          <option value="dismissed">Dismissed</option>
        </select>
        {view === 'feed' && (
          <select
            value={feedTargetFilter}
            onChange={(e) => {
              setFeedTargetFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none"
          >
            <option value="">All targets</option>
            <option value="post">Posts</option>
            <option value="comment">Comments</option>
            <option value="community">Communities</option>
          </select>
        )}
        {(view === 'safety' || view === 'safeMeet') && (
          <><select
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none"
          >
            <option value="">All priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select value={severityFilter} onChange={(e) => { setSeverityFilter(e.target.value); setPage(1); }} className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-pink-500 outline-none">
            <option value="">All severities</option><option value="critical">Critical</option><option value="urgent">Urgent</option><option value="routine">Routine</option>
          </select></>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : view === 'safety' ? (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Severity</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Reported user</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Reporter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statement</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {safetyReports.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No safety reports in this filter.
                  </td>
                </tr>
              ) : (
                safetyReports.map((row) => (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                          SEVERITY_STYLES[row.severity] || SEVERITY_STYLES.routine
                        }`}
                      >
                        {row.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">{formatType(row.reportType)}</td>
                    <td className="px-4 py-3">
                      <ReportedUserLink row={row} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {row.reportedBy?.profileName || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                      {row.statementPreview || row.reason || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {new Date(row.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => openSafetyDetail(row)}
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : view === 'users' ? (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold">User</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Email</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Count</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Latest</th>
                <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reportedUsers.map((item) => (
                <tr key={item.userId}>
                  <td className="px-6 py-4">
                    <Link to={`/users/${item.userId}`} className="font-semibold hover:text-pink-500">
                      {item.user?.profileName || 'Unknown'}
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{item.user?.email || 'N/A'}</td>
                  <td className="px-6 py-4">
                    <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                      {item.reportCount}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {item.latestReport
                      ? new Date(item.latestReport.timestamp).toLocaleDateString()
                      : 'N/A'}
                  </td>
                  <td className="px-6 py-4">
                    <button
                      type="button"
                      onClick={() => openUserModal(item)}
                      className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold"
                    >
                      View Reports
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : view === 'feed' ? (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Target</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Preview</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Author</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Reporter</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Reason</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {feedReports.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No feed reports in this filter.
                  </td>
                </tr>
              ) : (
                feedReports.map((row) => {
                  const authorId = row.reportedUserId || row.reportedUser?._id;
                  const reporterId = row.reporterId || row.reporter?._id;
                  const authorLabel =
                    row.reportedUser?.profileName ||
                    (row.reportedUser?.username ? `@${row.reportedUser.username}` : null) ||
                    (authorId ? String(authorId).slice(-6) : null);
                  const reporterLabel =
                    row.reporter?.profileName ||
                    (row.reporter?.username ? `@${row.reporter.username}` : null) ||
                    (reporterId ? String(reporterId).slice(-6) : null);
                  const postId = row.postId || (row.targetType === 'post' ? row.targetId : row.target?.postId);

                  return (
                  <tr key={row._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-semibold capitalize">
                      {row.targetType}
                      {row.target?.isDeleted ? (
                        <span className="ml-2 text-xs font-normal text-amber-700">(hidden/removed)</span>
                      ) : null}
                      {row.target?.missing ? (
                        <span className="ml-2 text-xs font-normal text-red-600">(missing)</span>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                      <div className="truncate" title={row.contentPreview || row.target?.preview || ''}>
                        {row.contentPreview || row.target?.preview || '—'}
                      </div>
                      {row.details ? (
                        <div className="text-xs text-gray-500 truncate mt-0.5">{row.details}</div>
                      ) : null}
                      {postId ? (
                        <Link
                          to={`/community-moderation?postId=${postId}`}
                          className="text-xs font-semibold text-blue-600 hover:underline mt-1 inline-block"
                        >
                          View post →
                        </Link>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {authorId ? (
                        <div>
                          <Link
                            to={`/users/${authorId}`}
                            className="font-medium text-pink-600 hover:underline"
                          >
                            {authorLabel || 'User'}
                          </Link>
                          {row.reportedUser?.username ? (
                            <div className="text-xs text-gray-500">@{row.reportedUser.username}</div>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {reporterId ? (
                        <div>
                          <Link
                            to={`/users/${reporterId}`}
                            className="font-medium text-pink-600 hover:underline"
                          >
                            {reporterLabel || 'User'}
                          </Link>
                          {row.reporter?.username ? (
                            <div className="text-xs text-gray-500">@{row.reporter.username}</div>
                          ) : null}
                        </div>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm capitalize">{(row.reason || '').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {row.createdAt ? new Date(row.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <select
                          value={row.status || 'pending'}
                          onChange={(e) =>
                            handleStatusUpdate(row._id, e.target.value, null, 'feed')
                          }
                          className="px-2 py-1 border rounded-lg text-sm"
                        >
                          <option value="pending">Pending</option>
                          <option value="resolved">Resolved</option>
                          <option value="dismissed">Dismissed</option>
                        </select>
                        {(row.targetType === 'post' || row.targetType === 'comment') &&
                          row.status === 'pending' &&
                          !row.target?.missing && (
                            <button
                              type="button"
                              onClick={() => {
                                if (
                                  window.confirm(
                                    row.targetType === 'post'
                                      ? 'Hide this feed post and mark report resolved?'
                                      : 'Delete this comment and mark report resolved?'
                                  )
                                ) {
                                  handleStatusUpdate(row._id, 'resolved', 'remove_content', 'feed');
                                }
                              }}
                              className="px-2 py-1 bg-red-100 text-red-700 rounded-lg text-sm font-semibold"
                            >
                              Remove & resolve
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <div key={report._id} className="bg-white rounded-2xl shadow-lg p-6 border">
              <h3 className="text-lg font-bold mb-2">{report.contentType || 'Content'} Report</h3>
              <p className="text-gray-700 mb-2">{report.reason}</p>
              <select
                value={report.status || 'pending'}
                onChange={(e) => {
                  const action = e.target.value === 'resolved' ? 'delete_content' : null;
                  handleStatusUpdate(report._id, e.target.value, action, 'content');
                }}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="pending">Pending</option>
                <option value="resolved">Resolved</option>
                <option value="dismissed">Dismissed</option>
              </select>
            </div>
          ))}
        </div>
      )}

      <div className="flex justify-center items-center gap-4 mt-6">
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="px-4 py-2 border rounded-xl disabled:opacity-50"
        >
          Previous
        </button>
        <span className="font-semibold">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          disabled={page === totalPages}
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          className="px-4 py-2 border rounded-xl disabled:opacity-50"
        >
          Next
        </button>
      </div>

      {showSafetyModal && selectedSafety && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold">Safety report review</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {formatType(selectedSafety.reportType)} · {selectedSafety.severity || selectedSafety.priority}
                </p>
              </div>
              <button type="button" onClick={() => setShowSafetyModal(false)} className="text-2xl text-gray-400">
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              {!safetyDetail ? (
                <p className="text-gray-500">Loading details…</p>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <strong>Reported user:</strong>{' '}
                      <ReportedUserLink row={safetyDetail.report} className="text-pink-600" />
                      {safetyDetail.report.accountDeleted && (
                        <p className="text-xs text-amber-700 mt-1">
                          Live profile removed. Use Deleted Accounts archive for messages and
                          report history. Resolve this case in the safety queue (status /
                          notes).
                        </p>
                      )}
                    </div>
                    <div>
                      <strong>Reporter:</strong> {safetyDetail.report.reportedBy?.profileName} (
                      {safetyDetail.report.reportedBy?.email})
                    </div>
                    <div>
                      <strong>Source:</strong> {safetyDetail.report.source}
                    </div>
                    <div>
                      <strong>Submitted:</strong>{' '}
                      {new Date(safetyDetail.report.createdAt).toLocaleString()}
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-xl">
                    <strong className="block mb-2">Statement</strong>
                    <p className="text-gray-800 whitespace-pre-wrap">
                      {safetyDetail.report.statement || safetyDetail.report.reason || '—'}
                    </p>
                  </div>

                  {safetyDetail.report.answers &&
                    Object.keys(safetyDetail.report.answers).length > 0 && (
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <strong className="block mb-2">Context answers</strong>
                        <pre className="text-xs overflow-auto bg-white p-3 rounded border">
                          {JSON.stringify(safetyDetail.report.answers, null, 2)}
                        </pre>
                      </div>
                    )}

                  {safetyDetail.report.evidenceConsent && safetyDetail.report.selectedEvidence?.length > 0 && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                      <strong className="block mb-2">Reporter-consented evidence</strong>
                      {safetyDetail.report.selectedEvidence.map((item) => <div key={item.messageId} className="text-sm bg-white p-2 rounded mb-2"><span className="text-xs text-gray-500">Message {item.messageId}</span><p className="whitespace-pre-wrap">{item.text}</p></div>)}
                    </div>
                  )}

                  <div className="p-4 bg-gray-50 rounded-xl">
                    <strong className="block mb-2">Immutable audit log</strong>
                    {safetyDetail.auditLog?.length ? safetyDetail.auditLog.map((entry, index) => {
                      const staff = auditText(entry?.staffId, ['email', 'name', 'profileName']);
                      const event = auditText(entry?.event);
                      const action = auditText(entry?.action) === '—' ? 'access' : auditText(entry?.action);
                      const reason = auditText(entry?.reason);
                      const createdAt = entry?.createdAt ? new Date(entry.createdAt) : null;
                      const timestamp = createdAt && !Number.isNaN(createdAt.getTime())
                        ? createdAt.toLocaleString()
                        : 'Unknown time';
                      return (
                        <div key={entry?._id || `${timestamp}-${index}`} className="text-xs border-b py-2">
                          {timestamp} · {staff} · {event} · {action}
                          {reason !== '—' ? ` — ${reason}` : ''}
                        </div>
                      );
                    }) : <p className="text-sm text-gray-500">No prior activity.</p>}
                  </div>

                  <div className="p-4 bg-blue-50 rounded-xl text-sm text-blue-900">
                    <strong>Interaction metadata (v1)</strong>
                    <ul className="mt-2 list-disc pl-5">
                      <li>Pokes between users: {numericMetric(safetyDetail.interactionMetadata?.pokeCountBetweenUsers)}</li>
                      <li>Profile views (reporter → reported):{' '}
                        {numericMetric(safetyDetail.interactionMetadata?.profileViewsByReporter)}
                      </li>
                    </ul>
                    <p className="mt-2 text-xs">{safetyDetail.interactionMetadata?.note}</p>
                  </div>

                  {safetyDetail.moderationHistory?.length > 1 && (
                    <div>
                      <strong className="text-sm">Other reports on this user</strong>
                      <ul className="mt-2 text-sm text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                        {safetyDetail.moderationHistory
                          .filter((h) => h._id !== selectedSafety._id)
                          .map((h) => (
                            <li key={h._id}>
                              {formatType(h.reportType)} — {h.status} —{' '}
                              {new Date(h.createdAt).toLocaleDateString()}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}

                  <div className="border-t pt-4 space-y-3">
                    <label className="block text-sm font-semibold">Admin notes</label>
                    <textarea
                      className="w-full border rounded-lg p-2 text-sm"
                      rows={3}
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                    />
                    <label className="block text-sm font-semibold">Action (human only)</label>
                    <select
                      className="w-full border rounded-lg p-2 text-sm"
                      value={actionTaken}
                      onChange={(e) => setActionTaken(e.target.value)}
                    >
                      <option value="">— Select action —</option>
                      <option value="dismiss">Dismiss</option>
                      <option value="warn">Warn (record only)</option>
                      <option value="request_info">Request more info</option>
                      <option value="require_verification">Require verification</option>
                      <option value="temp_suspension">Temporary suspend (reversible)</option>
                      <option value="temp_restriction">Restrict messaging / require review</option>
                      <option value="permanent_ban">Permanent ban (scrub data)</option>
                    </select>
                    <label className="block text-sm font-semibold">Status</label>
                    <select
                      className="w-full border rounded-lg p-2 text-sm"
                      value={safetyStatus}
                      onChange={(e) => setSafetyStatus(e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="reviewed">Reviewed</option>
                      <option value="action_taken">Action taken</option>
                      <option value="dismissed">Dismissed</option>
                    </select>
                    <button
                      type="button"
                      onClick={submitSafetyModeration}
                      className="w-full py-3 bg-pink-500 text-white font-semibold rounded-xl hover:bg-pink-600"
                    >
                      Save moderation decision
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between mb-4">
              <h2 className="text-xl font-bold">Reports for {selectedUser.user?.profileName}</h2>
              <button type="button" onClick={() => setShowUserModal(false)}>
                ×
              </button>
            </div>
            {selectedUser.reports.map((report) => (
              <div key={report._id} className="border rounded-lg p-4 mb-3">
                <p className="text-sm">
                  <strong>Type:</strong>{' '}
                  {report.type === 'safety'
                    ? `${formatType(report.reportType)} (${report.priority})`
                    : `${report.contentType} content`}
                </p>
                <p className="text-sm">
                  <strong>Reason:</strong> {report.reason}
                </p>
                {report.statement && (
                  <p className="text-sm mt-1">
                    <strong>Statement:</strong> {report.statement}
                  </p>
                )}
                <select
                  className="mt-2 border rounded p-1 text-sm"
                  value={(report.status || 'pending').toLowerCase()}
                  onChange={(e) =>
                    handleStatusUpdate(
                      report._id,
                      e.target.value,
                      null,
                      report.type === 'safety' ? 'safety' : 'content'
                    )
                  }
                >
                  <option value="pending">Pending</option>
                  <option value="dismissed">Dismissed</option>
                  <option value="action_taken">Action taken</option>
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Reports;
